import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import AppData from '../types/app-data.type';
import { debugMode } from '../config';
import { getLocalUserData } from './user.services';
import { isPlatform } from '@ionic/react';
import { fcmSubscribeToTopic } from '../functions/fcm.functions';

export const defaultData: AppData = {
	user: getLocalUserData(),
	isUserPaid: false,
	isAppReadyToSync: false
};

// Define types for the contexts
type AppContextType = AppData | null;
type AppContextUpdaterType = ((data: AppData) => void) | null;

const AppContext = createContext<AppContextType>(null);
const AppContextUpdater = createContext<AppContextUpdaterType>(null);

interface AppContextProviderProps {
	children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
	const [data, setData] = useState<AppData>(defaultData);
	const isMobileDevice = isPlatform('capacitor')

	const setAppData = (bookingData: AppData) => {
		setData(bookingData);
	};

	useEffect(() => {
		if (isMobileDevice) {
			// Request permission to use push notifications
			PushNotifications.requestPermissions().then(result => {
				if (result.receive === 'granted') {
					// Register with Apple / Google to receive push via APNS/FCM
					PushNotifications.register();
				} else {
					// Handle permission denied scenario
					console.warn("Push notification permissions not granted.");
				}
			});

			// On success, we should be able to receive notifications
			PushNotifications.addListener('registration', (token) => {
				debugMode && console.log('Push registration success, token: ' + token.value);
				fcmSubscribeToTopic('all-user')
			});
		}

		// Optional cleanup
		return () => {
			// Add cleanup logic if needed, e.g., unsubscribe from topic
		};
	}, []);

	return (
		<AppContext.Provider value={data}>
			<AppContextUpdater.Provider value={setAppData}>
				{children}
			</AppContextUpdater.Provider>
		</AppContext.Provider>
	);
};

export const useAppContext = (): AppData => {
	const store = useContext(AppContext);
	if (!store) {
		throw new Error('useAppContext must be used within a AppContextProvider.');
	}
	return store;
};

export const useAppContextUpdater = (): (data: AppData) => void => {
	const updater = useContext(AppContextUpdater);
	if (!updater) {
		throw new Error('useAppContextUpdater must be used within a AppContextProvider.');
	}
	return updater;
};
